import { useInternalState, assignMswData, serializeQuery } from '../hooks/internalState';
import {
  PostLeadCaptureResourceIdFormResult,
  PostLeadCaptureResourceIdFormOptions,
  PatchLeadCaptureResourceIdFormResult,
  PatchLeadCaptureResourceIdFormOptions,
  GetLeadCaptureResourceIdFormOptions,
  GetLeadCaptureResourceIdFormResult,
  getLeadCaptureResourceIdForm,
  PostLeadCaptureResourceIdFormFields,
  PatchLeadCaptureResourceIdFormFields,
  GetLeadCaptureResourceIdFormFields,
  postLeadCaptureResourceIdForm,
  patchLeadCaptureResourceIdForm,
} from '../fetchers/lead_capture/resource_id/form';
import { useCallback } from 'react';
import useSWR, { SWRConfiguration, useSWRConfig } from 'swr';
import { useGctlConfig } from '../hooks/GctlProvider';

/**
 * DO NOT MODIFY
 * Auto-generated by gctl-plugin-vimeo-hooks
 */

type UseGetLeadCaptureResourceIdFormOptions<Fields extends GetLeadCaptureResourceIdFormFields> =
  GetLeadCaptureResourceIdFormOptions<Fields>;

/**
 * Get information about a lead capture form for a resource
 * This method returns information about the lead capture form for the specified resource.
 */

export function useGetLeadCaptureResourceIdForm<Fields extends GetLeadCaptureResourceIdFormFields>(
  getOptions:
    | UseGetLeadCaptureResourceIdFormOptions<Fields>
    | (() => UseGetLeadCaptureResourceIdFormOptions<Fields> | null),
  config?: SWRConfiguration<GetLeadCaptureResourceIdFormResult<Fields>>,
) {
  const options = typeof getOptions === 'function' ? getOptions() : getOptions;

  let { baseUrl, jwt, xVimeoPage, locale } = useGctlConfig();

  return useSWR(
    options
      ? `/lead_capture/${options.where.resourceType}/${
          options.where.resourceId
        }/form${serializeQuery(options)}`
      : () => null,
    options
      ? () => {
          return getLeadCaptureResourceIdForm({
            ...options,
            headers: {
              ...options.headers,
              'Content-Type': 'application/json',
              Authorization: jwt ? `jwt ${jwt}` : '',
              'Vimeo-Page': `${xVimeoPage}`,
              'Accept-Language': locale ?? 'en',
            },
            baseUrl,
          });
        }
      : null,
    config,
  );
}

if (process.env.NODE_ENV !== 'production' || process.env.STORYBOOK === 'true') {
  assignMswData(useGetLeadCaptureResourceIdForm, {
    endpoint: '/lead_capture/:resourceType/:resourceId/form',
    method: 'GET',
  });
}

/**
 * Get information about a lead capture form for a resource
 * This method returns information about the lead capture form for the specified resource.
 */

export function useGetLeadCaptureResourceIdFormLazy<
  Fields extends GetLeadCaptureResourceIdFormFields,
>() {
  const { mutate } = useSWRConfig();
  let { baseUrl, jwt, xVimeoPage, locale } = useGctlConfig();

  let [state, dispatch] = useInternalState<
    GetLeadCaptureResourceIdFormResult<Fields> | undefined
  >();

  let callback = useCallback(
    async (options: UseGetLeadCaptureResourceIdFormOptions<Fields>) => {
      dispatch({ type: 'REQUEST' });

      try {
        let result = await mutate(
          `/lead_capture/${options.where.resourceType}/${
            options.where.resourceId
          }/form${serializeQuery(options)}`,
          getLeadCaptureResourceIdForm({
            ...options,
            baseUrl,
            headers: {
              ...options.headers,
              'Content-Type': 'application/json',
              Authorization: jwt ? `jwt ${jwt}` : '',
              'Vimeo-Page': `${xVimeoPage}`,
              'Accept-Language': locale ?? 'en',
            },
          }),
        );

        dispatch({ type: 'SUCCESS', payload: result });
      } catch (err) {
        dispatch({ type: 'FAILURE', payload: err });
      }
    },
    [baseUrl, xVimeoPage, jwt, locale, dispatch],
  );

  return [callback, state] as const;
}

if (process.env.NODE_ENV !== 'production' || process.env.STORYBOOK === 'true') {
  assignMswData(useGetLeadCaptureResourceIdFormLazy, {
    endpoint: '/lead_capture/:resourceType/:resourceId/form',
    method: 'GET',
  });
}

type UsePostLeadCaptureResourceIdFormOptions<Fields extends PostLeadCaptureResourceIdFormFields> =
  PostLeadCaptureResourceIdFormOptions<Fields>;

/**
 * Create a lead capture form for a resource
 * This method creates a lead capture form for the resource.
 */

export function usePostLeadCaptureResourceIdForm<
  Fields extends PostLeadCaptureResourceIdFormFields,
>() {
  const { mutate } = useSWRConfig();
  let { baseUrl, jwt, xVimeoPage, locale } = useGctlConfig();

  let [state, dispatch] = useInternalState<
    PostLeadCaptureResourceIdFormResult<Fields> | undefined
  >();

  let call = useCallback(
    async (options: UsePostLeadCaptureResourceIdFormOptions<Fields>) => {
      dispatch({ type: 'REQUEST' });

      try {
        let result = await mutate(
          `/lead_capture/${options.where.resourceType}/${
            options.where.resourceId
          }/form${serializeQuery(options)}`,
          postLeadCaptureResourceIdForm({
            ...options,
            baseUrl,
            headers: {
              ...options.headers,
              'Content-Type': 'application/json',
              Authorization: jwt ? `jwt ${jwt}` : '',
              'Vimeo-Page': `${xVimeoPage}`,
              'Accept-Language': locale ?? 'en',
            },
          }),
          false,
        );

        dispatch({ type: 'SUCCESS', payload: result });
      } catch (err) {
        dispatch({ type: 'FAILURE', payload: err });
      }
    },
    [baseUrl, xVimeoPage, jwt, locale, dispatch],
  );

  return [call, state] as const;
}

if (process.env.NODE_ENV !== 'production' || process.env.STORYBOOK === 'true') {
  assignMswData(usePostLeadCaptureResourceIdForm, {
    endpoint: '/lead_capture/:resourceType/:resourceId/form',
    method: 'POST',
  });
}

type UsePatchLeadCaptureResourceIdFormOptions<Fields extends PatchLeadCaptureResourceIdFormFields> =
  PatchLeadCaptureResourceIdFormOptions<Fields>;

/**
 * Update a lead capture form for a resource
 * This method updates the lead capture form for the resource.
 */

export function usePatchLeadCaptureResourceIdForm<
  Fields extends PatchLeadCaptureResourceIdFormFields,
>() {
  const { mutate } = useSWRConfig();
  let { baseUrl, jwt, xVimeoPage, locale } = useGctlConfig();

  let [state, dispatch] = useInternalState<
    PatchLeadCaptureResourceIdFormResult<Fields> | undefined
  >();

  let call = useCallback(
    async (options: UsePatchLeadCaptureResourceIdFormOptions<Fields>) => {
      dispatch({ type: 'REQUEST' });

      try {
        let result = await mutate(
          `/lead_capture/${options.where.resourceType}/${
            options.where.resourceId
          }/form${serializeQuery(options)}`,
          patchLeadCaptureResourceIdForm({
            ...options,
            baseUrl,
            headers: {
              ...options.headers,
              'Content-Type': 'application/json',
              Authorization: jwt ? `jwt ${jwt}` : '',
              'Vimeo-Page': `${xVimeoPage}`,
              'Accept-Language': locale ?? 'en',
            },
          }),
          false,
        );

        dispatch({ type: 'SUCCESS', payload: result });
      } catch (err) {
        dispatch({ type: 'FAILURE', payload: err });
      }
    },
    [baseUrl, xVimeoPage, jwt, locale, dispatch],
  );

  return [call, state] as const;
}

if (process.env.NODE_ENV !== 'production' || process.env.STORYBOOK === 'true') {
  assignMswData(usePatchLeadCaptureResourceIdForm, {
    endpoint: '/lead_capture/:resourceType/:resourceId/form',
    method: 'PATCH',
  });
}
